import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDropzone } from 'react-dropzone';
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { tripsService } from '../../services/supabase';
import { MultiCurrencyTotal, BigNumberCard, BudgetBar, UploadedFiles, RecentExpenses, SnapAPicComponent, RefundCard } from "./components";
import { LoadingView } from '@/components';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowRight, Calendar, Upload } from 'lucide-react';
import { groupExpensesByCurrency, calculateTotalInUSD } from '../../utils/currency';
import { useAuth } from '../../context/AuthContext';

import { ExepnseCounter } from "./components/ExepenseCounter";

export const TripDashboard = ():React.ReactNode => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { currentTrip } = useTrips();
  const { expenses, fetchTripExpenses } = useExpenses();
  const { firebaseUser } = useAuth();
  const [dashboard, setDashboard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Wait for currentTrip to be loaded (from DashboardLayout's fetchTripById)
      if (!currentTrip?.id) return;

      try {
        setLoading(true);
        setError(undefined);

        // Use currentTrip.id (the full UUID) for database queries
        const [dashboardData] = await Promise.all([
          tripsService.getTripDashboard(currentTrip.id),
          fetchTripExpenses(currentTrip.id),
        ]);

        setDashboard(dashboardData);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load trip data');
        setLoading(false);
      }
    };

    loadData();
  }, [currentTrip?.id, fetchTripExpenses]);

  const handleReceiptDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!firebaseUser || acceptedFiles.length === 0 || !currentTrip?.id) return;

    setIsUploadingReceipt(true);

    // Navigate to add-expense page with all files for processing
    navigate(`/trip/${tripId}/add-expense`, {
      state: { uploadedFiles: acceptedFiles }
    });
  }, [firebaseUser, currentTrip?.id, tripId, navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 10,
    maxSize: 10 * 1024 * 1024, // 10MB
    onDrop: handleReceiptDrop,
    noKeyboard: true
  });

  const getExpensesView = (): React.ReactNode => {
    if (!expenses.length) {
      return (
        <>
          {/* Email Forwarding Info Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3 py-4 bg-muted/50 rounded-lg">
                <Mail className="h-6 w-6 text-muted-foreground" />
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Forward expenses to:</div>
                  <div className="text-lg font-semibold">travel@thinkdifferentlabs.com</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      );
    }

    return (
      <div className="grid gap-4 md:gap-6 lg:gap-8 lg:grid-cols-4">
        {/* Recent Expenses - Takes 3/4 width */}
        <div className="lg:col-span-3">
          <RecentExpenses
            expenses={expenses}
            tripShortId={currentTrip?.short_id}
            maxExpenses={5}
          />
        </div>

        {/* Uploaded Files - Takes 1/4 width */}
        <div className="lg:col-span-1">
          <UploadedFiles expenses={expenses} maxFiles={5} />
        </div>
      </div>
    );
  };

  if (error) return <div>Error: {error}</div>;

  if (loading || !dashboard) return <LoadingView/>;

  // Calculate currency totals
  const currencyTotals = groupExpensesByCurrency(
    expenses.map(e => ({
      currency: e.currency || 'USD',
      amount: e.amount
    }))
  );

  const totalInUSD = calculateTotalInUSD(
    expenses.map(e => ({
      currency: e.currency || 'USD',
      amount: e.amount
    }))
  );

  // Calculate date range from expenses
  const getDateRange = () => {
    if (expenses.length === 0) return null;

    const dates = expenses.map(e => new Date(e.expense_date));
    const startDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const endDate = new Date(Math.max(...dates.map(d => d.getTime())));

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      start: startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      end: endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      days: diffDays + 1 // Include both start and end day
    };
  };

  const dateRange = getDateRange();

  return (
    <div className="flex flex-col gap-6 md:gap-8 overflow-x-hidden">
      <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <MultiCurrencyTotal currencyTotals={currencyTotals} />

        <BigNumberCard
          value={totalInUSD}
          label="Total (USD)"
        />

        {currentTrip?.use_budget && (
          <BudgetBar totalBudget={dashboard.total_budget} totalExpenses={dashboard.total_expenses} />
        )}

        {/* Refund Card */}
        <RefundCard />

        {/* Trip Duration Card */}
        {dateRange && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Trip Duration</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="text-3xl md:text-4xl font-bold">{dateRange.days}</div>
                  <div className="text-xs md:text-sm text-muted-foreground">day{dateRange.days !== 1 ? 's' : ''}</div>
                </div>
                <div className="border-l pl-3 space-y-1.5">
                  <div>
                    <div className="text-xs text-muted-foreground">Start</div>
                    <div className="text-xs md:text-sm font-semibold">{dateRange.start}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">End</div>
                    <div className="text-xs md:text-sm font-semibold">{dateRange.end}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Add Expense CTA */}
      <Card
        className="bg-primary/5 border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors"
        onClick={() => !isUploadingReceipt && navigate(`/trip/${tripId}/add-expense`)}
      >
        <CardContent className="py-6 md:py-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
            {/* Expense Counter */}
            <ExepnseCounter expenseCount={expenses.length} />

            {/* Text Content */}
            <div className="flex-1 text-center w-full sm:w-auto flex flex-col items-center">
              <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2">Add more expenses to track all</h3>
              <p className="text-xs md:text-sm text-muted-foreground w-[75%]">Upload receipts or manually add expenses to your trip</p>
            </div>

            {/* Upload buttons */}
            <div className="flex gap-3 flex-1 justify-center">
              {/* Camera Button - Always visible */}
              <div onClick={(e) => e.stopPropagation()} className="w-[135px]">
                <SnapAPicComponent
                  onCapture={async (file) => {
                    await handleReceiptDrop([file]);
                  }}
                  disabled={isUploadingReceipt}
                />
              </div>

              {/* Upload File - Always visible */}
              <div
                {...getRootProps()}
                onClick={(e) => {
                  e.stopPropagation();
                  // Trigger the file input click explicitly on mobile
                  const input = e.currentTarget.querySelector('input[type="file"]') as HTMLInputElement;
                  if (input && !isDragActive) {
                    input.click();
                  }
                }}
                className={`w-[135px] flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer ${
                  isDragActive
                    ? 'border-primary bg-primary/10'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                } ${isUploadingReceipt ? 'opacity-50 cursor-wait' : ''}`}
              >
                <input {...getInputProps()} />
                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                <span className="text-xs text-muted-foreground text-center whitespace-nowrap">
                  {isUploadingReceipt ? 'Uploading...' : 'Upload a file'}
                </span>
              </div>
            </div>

            {/* Arrow */}
            <ArrowRight className="hidden sm:block h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0" />
          </div>
        </CardContent>
      </Card>

      {getExpensesView()}
    </div>
  )
}

